import { useEffect, useRef } from 'react';

interface UseResponsiveSidebarProps {
  isSidebarExpanded: boolean;
  toggleSidebar: () => void;
  breakpoint?: number; // px value at which to auto-collapse
}

/**
 * Auto-collapses the sidebar when the viewport crosses below `breakpoint`.
 * Does not re-collapse after the user manually expands on a narrow viewport.
 */
export const useResponsiveSidebar = ({
  isSidebarExpanded,
  toggleSidebar,
  breakpoint = 1024, // Default to lg breakpoint (1024px)
}: UseResponsiveSidebarProps) => {
  const isExpandedRef = useRef(isSidebarExpanded);
  isExpandedRef.current = isSidebarExpanded;

  const toggleSidebarRef = useRef(toggleSidebar);
  toggleSidebarRef.current = toggleSidebar;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let previousWidth = window.innerWidth;

    // Collapse once on mount when already below the breakpoint.
    if (previousWidth < breakpoint && isExpandedRef.current) {
      toggleSidebarRef.current();
    }

    const handleResize = () => {
      const width = window.innerWidth;
      const crossedBelow = previousWidth >= breakpoint && width < breakpoint;
      previousWidth = width;

      if (crossedBelow && isExpandedRef.current) {
        toggleSidebarRef.current();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
};
